import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useTranslate } from '@growtherapy/i18n';
import { Tooltip } from '@growtherapy/sprout-ui';

import { SelfViewControlButton } from './SelfViewControlButton';

export type HideSelfViewButtonProps = {
  onClick: () => void;
  isVideoEnabled: boolean;
  isSelfViewHidden: boolean;
  position?: string;
  isMobile: boolean;
  isInSession: boolean;
};

export function HideSelfViewButton({
  onClick,
  isVideoEnabled,
  isSelfViewHidden,
  isMobile,
  isInSession,
}: HideSelfViewButtonProps) {
  const t = useTranslate();
  return (
    <div
      className={twMerge(
        classNames('z-10 hover:z-20 focus-within:z-20 flex items-center max-h-8', {
          'absolute top-2 right-2': !isSelfViewHidden,
          'relative m-2': isSelfViewHidden,
        }),
      )}
    >
      <HideSelfViewTooltip
        isSelfViewHidden={isSelfViewHidden}
        isVideoEnabled={isVideoEnabled}
        isInSession={isInSession}
      >
        <SelfViewControlButton
          buttonClassName={classNames('group/hide-self-view-button', {
            'opacity-100 w-auto border-neutral-900': isSelfViewHidden,
            'rebrand:w-450 rebrand:hover:w-auto rebrand:active:w-auto rebrand:focus:w-auto rebrand:focus-visible:w-auto':
              !isSelfViewHidden,
          })}
          icon={faEyeSlash}
          label={isSelfViewHidden ? t('telehealth-core:video.you') : t('telehealth-core:video.hideSelfView')}
          labelClassName={classNames({
            'group-hover/hide-self-view-button:block group-focus/hide-self-view-button:block': !isSelfViewHidden,
            inline: isSelfViewHidden,
          })}
          labelIsVisible={!isMobile || isSelfViewHidden}
          onClick={onClick}
          screenReaderLabel={
            isSelfViewHidden ? t('telehealth-core:video.showSelfView') : t('telehealth-core:video.hideSelfView')
          }
        />
      </HideSelfViewTooltip>
    </div>
  );
}

function HideSelfViewTooltip({
  isSelfViewHidden,
  isVideoEnabled,
  isInSession,
  children,
}: Pick<HideSelfViewButtonProps, 'isSelfViewHidden' | 'isVideoEnabled' | 'isInSession'> & {
  children: React.ReactElement;
}) {
  const t = useTranslate();
  const tooltipText = isInSession
    ? t('telehealth-core:video.othersCanStillSeeYourVideo')
    : t('telehealth-core:video.othersWillStillSeeYourVideo');

  // This state starts undefined so that the tooltip operates in an uncontrolled
  // manner (show on hover) until interacted with.
  const [initialShowTooltip, setInitialShowTooltip] = useState<boolean>();
  const tooltipTimeoutRef = useRef<number | null>(null);
  const [srAlert, setSrAlert] = useState<string>('');
  const srAlertTimeoutRef = useRef<number | null>(null);

  // We temporarily take control of the tooltip visibility to show it when the
  // user hides their self view, if they had video enabled. This is to help the
  // user understand that others can still see their video. After 5 seconds, we
  // hide the tooltip again. The tooltip's `afterHide` callback will be
  // immediately invoked when the tooltip is hidden, setting the state back to
  // `undefined` and thus returning the tooltip to its uncontrolled, default
  // (show on hover) behavior.
  useEffect(
    function manageSelfViewHiddenTooltip() {
      if (isSelfViewHidden && isVideoEnabled) {
        setInitialShowTooltip(true);
        // The SR alert needs to be added to the DOM after the live region, or
        // else it won't be read.
        srAlertTimeoutRef.current = window.setTimeout(() => {
          setSrAlert(tooltipText);
        }, 100);
        tooltipTimeoutRef.current = window.setTimeout(() => {
          setInitialShowTooltip(false);
        }, 5000);
      } else {
        setSrAlert('');
      }
      return () => {
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }
        if (srAlertTimeoutRef.current) {
          clearTimeout(srAlertTimeoutRef.current);
        }
      };
    },
    [isSelfViewHidden, isVideoEnabled, isInSession, tooltipText],
  );

  return (
    <>
      {isSelfViewHidden && isVideoEnabled && (
        <span className='sr-only' aria-live='polite'>
          {srAlert}
        </span>
      )}
      <Tooltip
        // NOTE: !w-max encourages the tooltip not to constrain its width to the
        // child `Button`'s width.
        className='!w-max'
        text={tooltipText}
        childIsInteractive={false}
        disabled={!isSelfViewHidden || !isVideoEnabled}
        isOpen={initialShowTooltip}
        // Reset the state to `undefined` after the tooltip is hidden to return it
        // to uncontrolled mode.
        afterHide={() => setInitialShowTooltip(undefined)}
      >
        {children}
      </Tooltip>
    </>
  );
}
